import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { MachineCard } from '@/components/MachineCard'
import { usePageSEO } from '@/hooks/usePageSEO'
import {
  getMachinesByCategory,
  type Machine,
  type MachineCategory,
} from '@/data/machines'

interface CategoryDescriptor {
  id: MachineCategory
  titleKey: string
  descKey: string
  eyebrowKey: string
  anchor: string
  index: string
}

const CATEGORIES: CategoryDescriptor[] = [
  {
    id: 'tangenziali',
    titleKey: 'machines.categories.tangential',
    descKey: 'machines.categories.tangentialDesc',
    eyebrowKey: 'machines.categories.tangentialEyebrow',
    anchor: 'tangenziali',
    index: '01',
  },
  {
    id: 'doppio-montante',
    titleKey: 'machines.categories.doubleColumn',
    descKey: 'machines.categories.doubleColumnDesc',
    eyebrowKey: 'machines.categories.doubleColumnEyebrow',
    anchor: 'doppio-montante',
    index: '02',
  },
]

export default function MachinesPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.machines.title'),
    description: t('meta.machines.description'),
    ogTitle: t('meta.machines.ogTitle'),
    ogDescription: t('meta.machines.ogDescription'),
    ogImage: '/images/machines/fenix-ff-u-200.jpg',
  })

  const totalMachines = CATEGORIES.reduce(
    (acc, c) => acc + getMachinesByCategory(c.id).length,
    0
  )

  return (
    <main id="main-content" className="bg-ink-100">
      {/* =====================================================
          HERO — industrial header with coordinate rail
          ===================================================== */}
      <PageHeader
        eyebrow={t('machines.categoriesTitle')}
        title={t('machines.title')}
        subtitle={t('machines.subtitle')}
        variant="dark"
      />

      {/* =====================================================
          CATEGORY INDEX RAIL — small engineered jump nav
          ===================================================== */}
      <nav
        aria-label={t('machines.categoriesTitle')}
        className="sticky top-[4.25rem] md:top-[5.25rem] z-20 border-b border-ink-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ul role="list" className="flex flex-wrap items-center gap-x-8 gap-y-3 py-3">
            <li className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-brand-600">
              <span
                aria-hidden="true"
                className="block h-px w-6 bg-brand-500"
              />
              {t('machines.categoriesTitle')}
            </li>
            {CATEGORIES.map((cat) => {
              const count = getMachinesByCategory(cat.id).length
              return (
                <li key={cat.id}>
                  <a
                    href={`#${cat.anchor}`}
                    className="group inline-flex items-center gap-3 text-sm font-semibold text-ink-700 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
                  >
                    <span
                      aria-hidden="true"
                      className="block text-[10px] font-bold tabular-nums tracking-widest text-ink-400 group-hover:text-brand-500"
                    >
                      {cat.index}
                    </span>
                    {t(cat.titleKey)}
                    <span
                      aria-hidden="true"
                      className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-sm bg-ink-100 px-1.5 text-[10px] font-bold tabular-nums text-ink-600 group-hover:bg-brand-500 group-hover:text-white transition-colors"
                    >
                      {count}
                    </span>
                  </a>
                </li>
              )
            })}
            <li className="ml-auto hidden sm:block">
              <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink-500 tabular-nums">
                {totalMachines} {t('machines.countLabel')}
              </span>
            </li>
          </ul>
        </div>
      </nav>

      {/* =====================================================
          CATEGORY SECTIONS — each with a dedicated header
          and a responsive card grid
          ===================================================== */}
      {CATEGORIES.map((cat, catIndex) => {
        const machinesInCategory = getMachinesByCategory(cat.id)
        const tone = catIndex % 2 === 0 ? 'default' : 'muted'
        return (
          <Section
            key={cat.id}
            id={cat.anchor}
            tone={tone}
            spacing="lg"
          >
            {/* Category header — engineered layout */}
            <header className="mb-10 md:mb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                {/* Index + eyebrow */}
                <div className="mb-4 flex items-center gap-4">
                  <span
                    aria-hidden="true"
                    className="block font-display text-2xl md:text-3xl font-bold tabular-nums text-brand-500 leading-none"
                  >
                    {cat.index}
                  </span>
                  <span
                    aria-hidden="true"
                    className="block h-px flex-1 max-w-[3rem] bg-brand-500"
                  />
                  <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-brand-700">
                    {t(cat.eyebrowKey)}
                  </span>
                </div>

                <h2 className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-2xl sm:text-3xl md:text-[2rem]">
                  {t(cat.titleKey)}
                </h2>

                <p className="mt-4 max-w-2xl fx-text-pretty text-base md:text-lg leading-relaxed text-ink-600">
                  {t(cat.descKey)}
                </p>
              </div>

              <div className="lg:col-span-4 flex lg:justify-end">
                <div className="inline-flex items-baseline gap-3 border-l-[3px] border-brand-500 pl-4">
                  <span className="font-display text-3xl md:text-4xl font-bold tabular-nums leading-none text-ink-900">
                    {machinesInCategory.length.toString().padStart(2, '0')}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ink-500">
                    {machinesInCategory.length === 1
                      ? t('machines.countLabelSingular')
                      : t('machines.countLabel')}
                  </span>
                </div>
              </div>
            </header>

            {/* Cards grid */}
            {machinesInCategory.length > 0 ? (
              <div
                role="list"
                aria-label={t(cat.titleKey)}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7"
              >
                {machinesInCategory.map((m: Machine) => (
                  <div role="listitem" key={m.slug}>
                    <MachineCard
                      slug={m.slug}
                      image={m.image}
                      imageAlt={
                        m.imageAltKey ? t(m.imageAltKey) : m.name
                      }
                      name={m.name}
                      description={t(m.shortDescriptionKey)}
                      category={t(cat.eyebrowKey)}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </Section>
        )
      })}

    </main>
  )
}
